import { create } from 'zustand';
import type { AlignmentStatus } from '../types/metro';

/**
 * Filter state shared across Map, Rankings, and Providers views so that
 * navigating between tabs preserves what the user is exploring.
 *
 * Keep this minimal — page-local UI state (collapsed sidebars, hovered rows)
 * stays in component state; only cross-page concerns belong here.
 */
interface FilterStore {
  readonly search: string;
  readonly statuses: ReadonlySet<AlignmentStatus>;
  readonly occupationalGroupId: string | null;
  /**
   * Continuous [lo, hi] filter on the credentials-to-jobs ratio. Currently
   * driven by the Map page's RatioRangeSlider (2026-07-02 Georgetown
   * feedback: reviewers wanted a slider instead of the binary
   * shortage/surplus chips). Rankings / Detail / Providers don't render
   * the slider yet, so on those pages the default range acts as a no-op
   * pass-through in `useFilteredMetros`. Bounds match the map legend's
   * gradient domain (0 → 2.0) to keep mental models consistent.
   */
  readonly ratioRange: readonly [number, number];
  setSearch: (search: string) => void;
  toggleStatus: (status: AlignmentStatus) => void;
  setOccupationalGroup: (id: string | null) => void;
  setRatioRange: (range: readonly [number, number]) => void;
  reset: () => void;
}

const ALL_STATUSES: ReadonlySet<AlignmentStatus> = new Set(['shortage', 'surplus']);

/**
 * Ratio slider bounds. 0.0 covers the deepest observed shortages
 * (~0.18 Houston in the sample data); 2.0 leaves headroom above the
 * strongest observed surpluses (~1.67 St. Louis) and matches the
 * legend gradient's upper anchor.
 */
export const RATIO_RANGE_BOUNDS: readonly [number, number] = [0, 2];

export const useFilterStore = create<FilterStore>((set) => ({
  search: '',
  statuses: ALL_STATUSES,
  occupationalGroupId: null,
  ratioRange: RATIO_RANGE_BOUNDS,
  setSearch: (search) => set({ search }),
  toggleStatus: (status) =>
    set((s) => {
      const next = new Set(s.statuses);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return { statuses: next };
    }),
  setOccupationalGroup: (id) => set({ occupationalGroupId: id }),
  setRatioRange: (range) => {
    // Clamp to bounds and enforce lo <= hi so callers can't wedge the
    // filter into an unreachable state via bad input.
    const [minB, maxB] = RATIO_RANGE_BOUNDS;
    const lo = Math.max(minB, Math.min(maxB, range[0]));
    const hi = Math.max(minB, Math.min(maxB, range[1]));
    set({ ratioRange: lo <= hi ? [lo, hi] : [hi, lo] });
  },
  reset: () =>
    set({
      search: '',
      statuses: ALL_STATUSES,
      occupationalGroupId: null,
      ratioRange: RATIO_RANGE_BOUNDS,
    }),
}));
